import React, { useEffect, useState } from "react";
import { List, Tag } from "antd";
import { ClockCircleOutlined } from "assets/icons";
import PageLayout from "layout/PageLayout/PageLayout";
import CustomBreadCrumb from "components/CustomBreadCrumb/CustomBreadCrumb";
import "./Crashes.css";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useSelector } from "react-redux";
import centralApi from "services/centralApi";
import { useLocation, useNavigate } from "react-router-dom";
import CrashDetailedView from "./CrashDetailedView/CrashDetailedView";
import CrashSessionCard from "./components/CrashSessionCard";
import CustomHighChart from "components/Charts/CustomHighChart/CustomHighChart";
import Selector from "components/Selector/Selector";
import SkeletonLoader from "components/SkeletonLoader/SkeletonLoader";
import { ApmApiMethods, ApmApiFields } from "utils/enums";
import { API_ENDPOINTS, apmTimeRange } from "utils/constants";
import { t } from "i18next";
const Crashes: React.FC = () => {
  dayjs.extend(relativeTime);
  const navigate = useNavigate();
  const location = useLocation();
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const [timeRange, setTimeRange] = useState(apmTimeRange.statsPeriod.default);
  const [events, setEvents] = useState<{
    data: any[];
    statsData: any;
    loading: {stats:boolean,data:boolean};
  }>({
    data: [],
    statsData: {},
    loading: {stats:true,data:true},
  });
  const [sessions, setSessions] = useState<{ data: any; loading: boolean }>({
    data: formatSessionData(undefined),
    loading: true,
  });
  const onIssueClick = (id: string) => {
    navigate(`${location.pathname}?id=${id}`);
  };
 /**
 * Formats raw session data into a structured format for display.
 */
function formatSessionData(sessions: any) {
  // Converts a decimal value to a percentage string (e.g., 0.92 -> "92.00%")
  const toPercentage = (value: number): string => {
    if (typeof value !== "number" || isNaN(value)) return "0.00%";
    return `${(value * 100).toFixed(2)}%`;
  };

  // Extract crash-free session and user rates
  const crashFreeSessionRate =
    sessions?.[ApmApiFields.CRASH_FREE_RATE_SESSION]?.groups?.[0]?.totals?.[
      ApmApiFields.CRASH_FREE_RATE_SESSION
    ];

  const crashFreeUserRate =
    sessions?.[ApmApiFields.CRASH_FREE_RATE_USER]?.groups?.[0]?.totals?.[
      ApmApiFields.CRASH_FREE_RATE_USER
    ];

  // Extract releases and get latest release version details
  const releases = sessions?.[ApmApiMethods.RELEASES_STATS] || [];
  const latestRelease = releases?.[0];
  const latestVersion = latestRelease?.version;

  // Format latest version label from version string
  const latestVersionLabel = latestVersion
    ? `${latestVersion.split("@")[1]?.split("+")[0]}(${latestVersion.split("+")[1]})`
    : undefined;

  // Extract crash-free session time series and intervals
  const crashFreeSessionSeries =
    sessions?.[ApmApiFields.CRASH_FREE_RATE_SESSION]?.groups?.[0]?.series?.[
      ApmApiFields.CRASH_FREE_RATE_SESSION
    ];
  const intervals = sessions?.[ApmApiFields.CRASH_FREE_RATE_SESSION]?.intervals;

  // Extract total session count
  const totalSessions =
    sessions?.[ApmApiFields.SUM_SESSION]?.groups?.[0]?.totals?.[
      ApmApiFields.SUM_SESSION
    ];

  // Return formatted data
  return {
    cards: [
      {
        label: t("crashFreeSessionsLabel"),
        value: toPercentage(crashFreeSessionRate),
      },
      {
        label: t("crashFreeUsersLabel"),
        value: toPercentage(crashFreeUserRate),
      },
      {
        label: t("numberOfReleasesLabel"),
        value: `${releases.length}`,
        children: latestRelease
          ? {
              label: t("latestLabel"),
              value: latestVersionLabel,
            }
          : undefined,
      },
    ],
    crash_free_session: {
      values: crashFreeSessionSeries,
      intervals: intervals,
      total: totalSessions,
    },
  };
}

/**
 * Fetches session-related metrics and updates the state with the formatted result.
 */
  const getSessions = async () => {
  try {
    // Set loading to true before starting the API calls
    setSessions((prevState) => ({ ...prevState, loading: true }));

    // Define the list of fields and corresponding API methods to fetch data for
    const fields = [
      {
        field: ApmApiFields.CRASH_FREE_RATE_SESSION,
        api_method: ApmApiMethods.SESSIONS,
      },
      {
        field: ApmApiFields.CRASH_FREE_RATE_USER,
        api_method: ApmApiMethods.SESSIONS,
      },
      {
        field: ApmApiFields.SUM_SESSION,
        api_method: ApmApiMethods.SESSIONS,
      },
      {
        api_method: ApmApiMethods.RELEASES_STATS, // No specific field for this one
      },
    ];

    // Map over each field and create an API call for it
    const promises = fields.map(async (payload) => {
      const params = {
        api_key: loginUser.api_key, 
        app_id: appId, 
        statsPeriod: timeRange, // Selected time range
        interval: apmTimeRange.interval[ApmApiMethods.SESSIONS][timeRange], // Interval mapping based on time range
        ...payload, // Spread the rest of the payload (field & api_method)
      };

      try {
        // Make POST request to fetch session data
        const sessions = await centralApi(
          "POST",
          API_ENDPOINTS.APM_METRICS_URL,
          params
        );
        // Return result with the field as key
        return { field: payload.field || payload.api_method, data: sessions };
      } catch (error) {
        // Handle individual API call failure gracefully by returning null
        return { field: payload.field || payload.api_method, data: null };
      }
    });

    // Wait for all API calls to complete
    const resultArray = await Promise.all(promises);

    // Convert the result array to an object keyed by field
    const result = resultArray.reduce((acc, { field, data }) => {
      acc[field] = data;
      return acc;
    }, {} as Record<string, any>);

    // Update sessions state with formatted data and set loading to false
    setSessions((prevState) => ({
      ...prevState,
      data: formatSessionData(result),
      loading: false,
    }));
  } catch (error) {
    // Handle unexpected errors and ensure loading is turned off
    setSessions((prevState) => ({ ...prevState, data: {}, loading: false }));
  }
};

 /**
 * Fetches event data from the APM metrics API and updates the state accordingly.
 */
const getEvents = async () => {
  try {
    // Set the 'data' loading flag to true before the API call
    setEvents((prevState) => ({
      ...prevState,
      loading: { ...prevState.loading, data: true },
    }));

    // Prepare API request parameters
    const params = {
      api_key: loginUser.api_key,
      app_id: appId,
      statsPeriod: timeRange,
      api_method: ApmApiMethods.EVENTS,
    };

    // Make API call to fetch event data
    const data = await centralApi(
      "POST",
      API_ENDPOINTS.APM_METRICS_URL,
      params
    );

    // Update state with the fetched data and set loading to false
    setEvents((prevState) => ({
      ...prevState,
      data: Array.isArray(data) ? data : [],
      loading: { ...prevState.loading, data: false },
    }));
  } catch {
    // On error, reset data to an empty array and turn off loading
    setEvents((prevState) => ({
      ...prevState,
      data: [],
      loading: { ...prevState.loading, data: false },
    }));
  }
};

  /**
 * Fetches event statistics and metadata in parallel, then updates the state.
 */
const getEventStats = async () => {
  try {
    // Set the 'stats' loading flag to true before the API call
    setEvents((prevState) => ({
      ...prevState,
      loading: { ...prevState.loading, stats: true },
    }));

    const fields = [ApmApiMethods.EVENT_STATS, ApmApiMethods.EVENTS_META];

    // Common API parameters for both methods
    const params = {
      api_key: loginUser.api_key,
      app_id: appId,
      statsPeriod: timeRange,
      interval: apmTimeRange.interval[ApmApiMethods.EVENT_STATS][timeRange],
      dataset: "errors",
      yAxis: ApmApiFields.COUNT,
      field: ApmApiFields.COUNT,
      query: "!event.type:transaction",
      referrer: "api.organization-event-stats",
      partial: 1,
    };

    // Make parallel API calls for EVENT_STATS and EVENTS_META
    const data = Object.fromEntries(
      await Promise.all(
        fields.map(async (method) => {
          const result = await centralApi(
            "POST",
            API_ENDPOINTS.APM_METRICS_URL,
            {
              ...params,
              api_method: method,
            }
          );
          return [method, result];
        })
      )
    );

    // Update the state with received data and stop loading
    setEvents((prevState) => ({
      ...prevState,
      statsData: {
        data: data[ApmApiMethods.EVENT_STATS]?.data,
        count: data[ApmApiMethods.EVENTS_META]?.count,
      },
      loading: { ...prevState.loading, stats: false },
    }));
  } catch {
    // On error, clear statsData and stop loading
    setEvents((prevState) => ({
      ...prevState,
      statsData: {},
      loading: { ...prevState.loading, stats: false },
    }));
  }
};

  useEffect(() => {
    if (!location.search) {
      getSessions();
      getEvents();
      getEventStats();
    }
  }, [location, timeRange]);
  return !location.search ? (
    <PageLayout
     title={`${t("analyzeLabel")}`}
      className="crash-analytics-main-dashboard-container"
      topLeftItem={
        <CustomBreadCrumb
          items={[
            {
              title: <span>{t("issuesLabel")}</span>,
            },
          ]}
        />
      }
      topRightItem={
        <div>
          <span className="me-2">{t("timeRangeLabel")}</span>
          <Selector
            style={{ width: 150 }}
            value={timeRange}
            onChange={(value) => {
              setTimeRange(value);
            }}
            options={apmTimeRange.statsPeriod.options}
          />
        </div>
      }
    >
      <div className="d-flex w-100 gap-3 pb-3 ">
        {sessions.data?.cards?.map(
          ({
            label,
            value,
            children,
          }: {
            label: string;
            value: string;
            children: { label: string; value: string };
          }) => {
            return (
              <CrashSessionCard
                title={label}
                percentage={value}
                metaData={children}
                loading={sessions.loading}
              />
            );
          }
        )}
      </div>

      <div className="crash-analytics-session-chart-container mb-3">
        <div className="d-flex justify-content-between">
          <strong>{t("crashFreeSessionsLabel")}</strong>
          {sessions.loading ? (
            <SkeletonLoader type="button" />
          ) : (
            <strong>
              {t("totalLabel")}: {sessions.data?.crash_free_session?.total}
            </strong>
          )}
        </div>
        <div className="mt-3">
          <CustomHighChart
            options={{
              loading: sessions.loading,
              chart: {
                type: "line",
                zoomType: "x",
                height: 300,
              },
              title: {
                text: "",
              },
              time: {
                timezoneOffset: -330, // minus because it's ahead of UTC
              },
              xAxis: {
                tickLength: 0,
                type: "datetime",
                title: { text: null },
                labels: {
                  format: `{value:${
                    timeRange.includes("d") ? "%b %e" : ""
                  } %I:%M %p}`,
                },
              },
              navigation: {
                buttonOptions: {
                  enabled: false, // Set to false to hide the hamburger icon
                },
              },
              yAxis: {
                title: undefined,
                min: 0,
                max: 100,
                labels: {
                  format: "{value}%",
                },
                plotLines: [
                  {
                    value: 0,
                    color: "#999",
                    width: 1,
                    dashStyle: "ShortDash",
                  },
                ],
              },
              tooltip: {
                shared: true,
                crosshairs: true,
                valueSuffix: "%",
                xDateFormat: "%b %e %Y, %I:%M %p",
              },
              legend: {
                enabled: false,
              },
              series: [
                {
                  name: t("thisPeriodLabel"),
                  data: sessions.data.crash_free_session?.intervals?.map(
                    (ts: number, i: number) => [
                      new Date(ts).getTime(),
                      sessions.data.crash_free_session?.values[i] * 100,
                    ]
                  ),
                  color: "#2f7ed8",
                  lineWidth: 2,
                  marker: {
                    enabled: false,
                    turboThreshold: 0,
                  },
                },
              ],
              credits: {
                enabled: false,
              },
            }}
          />
        </div>
      </div>

      <div className="crash-analytics-session-chart-container mb-3">
        <div className="d-flex justify-content-between">
          <strong>{t("numberOfErrorsLabel")}</strong>
          {events.statsData.loading ? (
            <SkeletonLoader type="button" />
          ) : (
            <strong>
              {t("totalLabel")}: {events?.statsData?.count}
            </strong>
          )}
        </div>
        <div className="mt-3">
          <CustomHighChart
            options={{
              loading: events.loading.stats,
              chart: {
                type: "column",
                height: 300,
              },
              title: {
                text: undefined,
              },
              xAxis: {
                type: "datetime",
                tickLength: 0,
                title: {
                  text: undefined,
                },
                labels: {
                  format: `{value:${
                    timeRange.includes("d") ? "%b %e" : ""
                  } %I:%M %p}`,
                },
              },
              tooltip: {
                shared: true,
                crosshairs: true,
                xDateFormat: "%b %e,%Y %I:%M %p",
              },
              legend: {
                enabled: false,
              },
              navigation: {
                buttonOptions: {
                  enabled: false,
                },
              },
              time: {
                timezoneOffset: -330, // minus because it's ahead of UTC
              },
              yAxis: {
                min: 0,
                title: {
                  text: undefined,
                },
              },
              series: [
                {
                  name: t("thisPeriodLabel"),
                  data: events?.statsData?.data?.map(
                    ([timestamp, data]: [number, { count: number }[]]) => [
                      Number(timestamp) * 1000,
                      data[0]?.count ?? 0,
                    ]
                  ),
                },
              ],
            }}
          />
        </div>
      </div>
        <List
          itemLayout="vertical"
          loading={events.loading.data}
          dataSource={events.data}
          pagination={{ pageSize: 10, size: "small", simple: false }}
          renderItem={(item) => (
            <div
              className="crash-card-main-container mb-2 rounded p-3"
              key={item?.id}
              onClick={() => {
                onIssueClick(item?.id);
              }}
            >
              <div className="d-flex gap-2 mb-2">
                {<Tag color="red">{item.metadata?.type || item.title}</Tag>}
                <span className="text-muted">{item?.culprit}</span>
              </div>

              <div className="mb-2 d-flex gap-2 align-items-center">
                <span className="crash-card-error-dot"></span>
                <strong>{item.metadata?.value}</strong>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-2">
                <div className="d-flex align-items-center gap-1">
                  <ClockCircleOutlined />{" "}
                  <span>{dayjs(item?.dateCreated).fromNow()}</span>
                </div>
              </div>
            </div>
          )}
        />
    </PageLayout>
  ) : (
    <CrashDetailedView/>
  );
};
export default Crashes;
